import React, { useState, useRef, useEffect } from 'react';
import BigNumber from 'bignumber.js';
import { isValidNumber } from '../../utils/numberFormatting';

// A bare, in-text editable number for the Pocket calculator — looks like plain
// text (no box), parses to a BigNumber on commit, and clamps to an optional
// minimum. Mirrors the parsing rules of the boxed NumberInput/Parameter so
// editing behaves the same, just visually flat.
const PocketField = ({ value, onCommit, min, decimals = 8, prefix = '', suffix = '', align = 'left', allowNegative = false, className = '' }) => {
  const toStr = (v) => {
    if (v === undefined || v === null || v === '') return '';
    try { const bn = new BigNumber(v); return bn.isNaN() ? '' : bn.toString(); } catch { return ''; }
  };
  // With negatives allowed (e.g. forex swap), validate via BigNumber since the
  // boxed isValidNumber helper is tuned for unsigned inputs.
  const valid = (s) => (s !== '' && (allowNegative ? !new BigNumber(s).isNaN() : isValidNumber(s)));

  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const ref = useRef(null);

  useEffect(() => { if (!editing) setRaw(toStr(value)); }, [value, editing]);

  const display = editing ? raw : `${prefix}${toStr(value)}${suffix}`;

  const commit = () => {
    setEditing(false);
    if (valid(raw)) {
      let bn = new BigNumber(raw);
      if (min !== undefined && bn.isLessThan(min)) bn = new BigNumber(min);
      onCommit(bn);
    } else {
      setRaw(toStr(value));
    }
  };

  const onInput = (e) => {
    let v = e.target.value.replace(/,/g, '.');
    if (allowNegative) {
      const neg = v.trim().startsWith('-');
      v = v.replace(/[^0-9.]/g, '');
      if (neg) v = '-' + v;
    } else {
      v = v.replace(/[^0-9.]/g, '');
    }
    const sign = v.startsWith('-') ? '-' : '';
    let body = sign ? v.slice(1) : v;
    const parts = body.split('.');
    if (parts.length > 2) body = parts[0] + '.' + parts.slice(1).join('');
    if (body.includes('.')) {
      const [i, d] = body.split('.');
      if (d && d.length > decimals) body = i + '.' + d.slice(0, decimals);
    }
    v = sign + body;
    setRaw(v);
    if (valid(v)) {
      const bn = new BigNumber(v);
      if (!(min !== undefined && bn.isLessThan(min))) onCommit(bn);
    }
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={() => { setEditing(true); setRaw(toStr(value)); }}
      onChange={onInput}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setRaw(toStr(value)); setEditing(false); e.target.blur(); } }}
      size={Math.max(1, display.length + 1)}
      className={`pk-mono bg-transparent border-0 outline-none tabular-nums text-[#eef3f8] font-normal px-1 -mx-1 rounded hover:bg-white/[0.06] focus:bg-white/[0.09] ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
      style={{ width: 'auto' }}
    />
  );
};

export default PocketField;
